import { formatAddressInline } from "../../../../../../../../../_shared/_logic/countries"
import { filterActiveOrganizationUsers } from "../../../../../../../_shared/_logic/organization-users"

export const formatAddress = (property?: Property | null) => {
  if (!property) return "Unbekannte Adresse"

  return formatAddressInline(property) || "Unbekannte Adresse"
}

export const normalizePath = (path?: string | null) => {
  let trimmed = path || "/"
  while (trimmed.length > 1 && trimmed.endsWith("/")) {
    trimmed = trimmed.slice(0, -1)
  }

  return trimmed || "/"
}

export const visibleScheduleEventsForWorkOrder = (
  workOrderId: string | null,
  events: ScheduleEvent[],
  removedIds: string[],
) =>
  workOrderId
    ? events
        .filter((entry) => entry.work_order_id === workOrderId)
        .filter((entry) => !["CANCELED", "NOSHOW"].includes(entry.status))
        .filter((entry) => !removedIds.includes(entry.id ?? ""))
    : []

type MergeArgs = {
  currentAll: ScheduleEvent[]
  incoming: ScheduleEvent[]
  removedIds: string[]
  workOrderId?: string | null
  replace?: boolean
}

export const mergeScheduleEvents = ({
  currentAll,
  incoming,
  removedIds,
  workOrderId = null,
  replace = false,
}: MergeArgs) => {
  const base = replace ? [] : currentAll
  const map = new Map<string, ScheduleEvent>()
  base.forEach((evt) => {
    if (!evt?.id || removedIds.includes(evt.id)) return

    map.set(evt.id, evt)
  })
  incoming.forEach((evt) => {
    if (!evt?.id || removedIds.includes(evt.id)) return

    map.set(evt.id, evt)
  })
  const all = Array.from(map.values())
  const visible = visibleScheduleEventsForWorkOrder(workOrderId, all, removedIds)

  return { all, visible }
}

export const markRemovedScheduleEventIds = (removedIds: string[], id?: string | null) => {
  if (!id || removedIds.includes(id)) return removedIds

  return [...removedIds, id]
}

export const clearRemovedScheduleEventIds = (removedIds: string[], id?: string | null) => {
  if (!id) return removedIds

  return removedIds.filter((entry) => entry !== id)
}

export type EmployeeSource = {
  id?: string | null
  is_active?: boolean | null
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}

const resolveUserLabel = (user: EmployeeSource) => {
  const nameFromParts = [user.first_name, user.last_name].filter(Boolean).join(" ").trim()
  const label = (user.username ?? "").trim() || nameFromParts || (user.email ?? "").trim()

  return label || `Mitarbeiter ${String(user.id ?? "").slice(0, 8)}`
}

const addUniqueEmployee = (
  map: Map<string, { id: string; label: string; shortLabel?: string }>,
  id?: string | null,
  label?: string | null,
) => {
  const cleanId = (id ?? "").trim()

  if (!cleanId || map.has(cleanId)) return

  const resolvedLabel = (label ?? "").trim() || `Mitarbeiter ${cleanId.slice(0, 8)}`
  const shortLabel = (resolvedLabel.split(/\s+/)[0] || resolvedLabel).trim()
  map.set(cleanId, { id: cleanId, label: resolvedLabel, shortLabel })
}

export const buildEmployeeOptions = (
  users: EmployeeSource[] = [],
  fallbackEvents: ScheduleEvent[] = [],
) => {
  const map = new Map<string, { id: string; label: string; shortLabel?: string }>()

  filterActiveOrganizationUsers(users).forEach((user) => {
    if (!user?.id) return

    const label = resolveUserLabel(user)
    addUniqueEmployee(map, user.id, label)
  })

  fallbackEvents.forEach((evt) => addUniqueEmployee(map, evt.employee_id, evt.employee_name))

  return Array.from(map.values())
}
